"use client"

import * as React from "react"
import { cn } from "cn"

import { Button } from "./button"

/**
 * An answer the user gives in a conversation, such as "Yes, that's right": lighter than the page's buttons, with
 * regular weight and no shadow. quiet drops the outline too, for a pass like "Skip".
 */
function Reply({
  quiet = false,
  className,
  ...props
}: Omit<React.ComponentProps<typeof Button>, "variant" | "shape" | "size"> & {
  quiet?: boolean
}) {
  return (
    <Button
      data-slot="reply"
      data-quiet={quiet || undefined}
      variant={quiet ? "ghost" : "outline"}
      shape="pill"
      className={cn(
        "h-auto min-h-9 max-w-full justify-start py-1.5 text-start font-normal whitespace-normal shadow-none",
        className
      )}
      {...props}
    />
  )
}

/** Replies in a row; when they don't fit on one line they stack, and a quiet one goes under them as one more line. */
function Replies({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  const row = React.useRef<HTMLDivElement>(null)
  const [stacked, setStacked] = React.useState(false)
  const measure = React.useCallback(() => {
    const element = row.current
    if (!element) return
    const replies = Array.from(
      element.querySelectorAll<HTMLElement>(
        "[data-slot=reply]:not([data-quiet])"
      )
    )
    const gap = parseFloat(getComputedStyle(element).columnGap) || 0
    const width =
      replies.reduce((total, reply) => total + reply.offsetWidth, 0) +
      gap * (replies.length - 1)
    setStacked(width > element.clientWidth)
  }, [])
  React.useLayoutEffect(measure)
  React.useEffect(() => {
    const element = row.current
    if (!element) return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [measure])

  return (
    <div
      ref={row}
      data-slot="replies"
      data-stacked={stacked || undefined}
      className={cn(
        "flex gap-2",
        stacked ? "flex-col items-start" : "flex-wrap items-center",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export { Replies, Reply }
